import React from 'react';
import { motion } from 'framer-motion';
import BrutalButton from './BrutalButton';

const Hero: React.FC = () => {
  return (
    <section className="relative min-h-screen flex flex-col justify-center items-center pt-20 overflow-hidden border-b-4 border-brand-red">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-grid-pattern bg-[length:40px_40px] opacity-15 pointer-events-none" />
      
      {/* Data Visualization Background — brand-consistent */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute right-0 top-0 w-1/2 h-full opacity-[0.07] bg-cover bg-center mask-l-from-50%"
          style={{ backgroundImage: 'url(/images/hero-data-viz.png)' }}
        />
      </div>
      
      {/* Corner accents */}
      <div className="absolute top-20 left-6 w-16 h-16 border-l-2 border-t-2 border-brand-red/40 hidden md:block" />
      <div className="absolute top-20 right-6 w-16 h-16 border-r-2 border-t-2 border-brand-red/40 hidden md:block" />
      <div className="absolute bottom-24 left-6 w-16 h-16 border-l-2 border-b-2 border-brand-red/40 hidden md:block" />
      <div className="absolute bottom-24 right-6 w-16 h-16 border-r-2 border-b-2 border-brand-red/40 hidden md:block" />

      <div className="container mx-auto px-4 md:px-8 z-10 relative text-center">
        {/* Status badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-red/10 border border-brand-red/30 text-brand-red font-mono text-xs tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 bg-brand-red rounded-full animate-pulse" />
            Enterprise Performance Marketing
          </span>
        </motion.div>

        {/* Main headline */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className="text-[11vw] md:text-[8vw] leading-[0.88] font-black tracking-[-0.03em] text-brand-white select-none mb-2">
            WE BUILD
          </h1>
          <h1 className="text-[11vw] md:text-[8vw] leading-[0.88] font-black tracking-[-0.03em] select-none mb-2">
            <span className="text-brand-red">REVENUE</span>
          </h1>
          <h1 className="text-[11vw] md:text-[8vw] leading-[0.88] font-black tracking-[-0.03em] text-brand-white select-none">
            MACHINES.
          </h1>
        </motion.div>

        {/* Subheadline */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="max-w-2xl mx-auto text-base md:text-lg font-mono text-gray-400 mt-8 mb-12 leading-relaxed"
        >
          Not another agency. We engineer performance marketing systems 
          that convert ad spend into measurable revenue. 
          Built for enterprises doing <span className="text-brand-white font-bold">40Cr+</span>.
        </motion.p>

        {/* CTA Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <BrutalButton onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth'})}>
            START DOMINATING
          </BrutalButton>
          <BrutalButton variant="outline" href="/manifesto">
            READ OUR MANIFESTO
          </BrutalButton>
        </motion.div>

        {/* Trust signals */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-16 flex flex-wrap justify-center gap-x-8 gap-y-3 font-mono text-xs text-gray-500 tracking-wider uppercase"
        >
          <span>50+ Enterprises Served</span>
          <span className="hidden sm:inline text-brand-gray">|</span>
          <span>₹142Cr+ Revenue Generated</span>
          <span className="hidden sm:inline text-brand-gray">|</span>
          <span>India • UAE • KSA</span>
        </motion.div>
      </div>

      {/* Marquee Tape */}
      <div className="absolute bottom-0 w-full bg-brand-white transform -rotate-1 py-2.5 overflow-hidden border-y-4 border-brand-red">
        <div className="whitespace-nowrap animate-marquee flex space-x-8">
           {Array(20).fill(null).map((_, i) => (
             <span key={i} className="text-black font-black text-xl md-2xl font-sans tracking-widest inline-flex items-center gap-4">
               E3 METHODOLOGY <span className="text-brand-red text-sm">///</span> EMBEDDED <span className="text-brand-red text-sm">///</span> ESSENTIAL <span className="text-brand-red text-sm">///</span> ENGINEERED <span className="text-brand-red text-sm">///</span>
             </span>
           ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
